import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const CreditGuardContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export function CreditGuardProvider({ children }) {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [credits, setCredits] = useState(null);
  const [creditsExempt, setCreditsExempt] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  const fetchCredits = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/billing/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCredits(res.data.credits);
      setCreditsExempt(res.data.credits_exempt || false);
    } catch {
      // ignore
    }
  }, [token]);

  // Fetch credits on mount and when token changes
  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  // Redirect to payment gate when credits drop to 0
  // Skip redirect for credits-exempt users
  // Allowed pages at 0 credits: /payment-gate, /portal/profile (for delete account)
  useEffect(() => {
    if (user?.role === 'admin' || credits === null || credits > 0 || creditsExempt) return;
    const allowed = ['/payment-gate', '/portal/profile', '/portal/billing', '/login', '/register', '/verify-2fa'];
    const isAllowed = (Array.isArray(allowed) ? allowed : []).some(p => location.pathname.startsWith(p));
    if (!isAllowed && location.pathname !== '/') {
      navigate('/payment-gate', { replace: true });
    }
  }, [credits, creditsExempt, location.pathname, navigate, user?.role]);

  // Global axios interceptor for 402 responses
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 402 && user?.role !== 'admin') {
          fetchCredits();
          navigate('/payment-gate', { replace: true });
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [fetchCredits, navigate, user?.role]);

  // Refresh credits after any custom event (e.g., after purchase)
  useEffect(() => {
    const handler = () => fetchCredits();
    window.addEventListener('credits-updated', handler);
    return () => window.removeEventListener('credits-updated', handler);
  }, [fetchCredits]);

  const dismissModal = () => {
    setShowModal(false);
    setModalData(null);
  };

  return (
    <CreditGuardContext.Provider value={{ credits, fetchCredits, showModal, modalData, dismissModal }}>
      {children}
    </CreditGuardContext.Provider>
  );
}

export function useCreditGuard() {
  return useContext(CreditGuardContext);
}
